import { useState, useEffect } from 'react';
import {
  TrendingUp,
  Users,
  Activity,
  Database,
  RefreshCw,
  Download,
} from 'lucide-react';
import { useUsageAnalytics } from '../../../hooks/platform-admin/useUsageAnalytics';
import { UsageSummary, TopConsumer } from '../../../services/platform-admin/usage-analytics.service';

export default function UsageAnalyticsPage() {
  const { getSummary, getTopConsumers, isLoading, error } = useUsageAnalytics();

  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [topConsumers, setTopConsumers] = useState<TopConsumer[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string>('apiCalls');

  const loadData = async () => {
    const summaryData = await getSummary();
    if (summaryData) {
      setSummary(summaryData);
    }

    const consumersData = await getTopConsumers(selectedMetric);
    if (consumersData) {
      setTopConsumers(consumersData);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedMetric]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    if (bytes >= 1073741824) {
      return `${(bytes / 1073741824).toFixed(2)} GB`;
    }
    if (bytes >= 1048576) {
      return `${(bytes / 1048576).toFixed(2)} MB`;
    }
    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${bytes} B`;
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Analytics</h1>
          <p className="text-sm text-gray-600 mt-1">
            Platform-wide usage metrics and top consumers
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadData}
            disabled={isLoading}
            className="flex items-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-700 hover:to-red-700 transition-all"
          >
            <Download className="h-4 w-4" />
            Export Report
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Total Organizations</div>
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {summary.totalOrganizations}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {summary.activeOrganizations} active
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Total Users</div>
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {formatNumber(summary.totalUsers)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {formatNumber(summary.totalAgents)} agents
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">API Calls (24h)</div>
                <Activity className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {formatNumber(summary.last24Hours.apiCalls)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {formatNumber(summary.last24Hours.webhooks)} webhooks
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Storage Used</div>
                <Database className="h-5 w-5 text-orange-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {formatBytes(summary.last24Hours.storage)}
              </div>
              <div className="text-sm text-gray-500 mt-1">Last 24 hours</div>
            </div>
          </div>

          {/* Top Consumers Section */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Top Consumers</h2>
                    <p className="text-sm text-gray-600">
                      Organizations with highest resource usage
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700">Metric:</label>
                  <select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="apiCalls">API Calls</option>
                    <option value="webhooks">Webhooks</option>
                    <option value="users">Users</option>
                    <option value="agents">Agents</option>
                    <option value="storage">Storage</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      API Calls
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Webhooks
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Users
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agents
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Storage
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                        Loading top consumers...
                      </td>
                    </tr>
                  ) : topConsumers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                        No data available
                      </td>
                    </tr>
                  ) : (
                    topConsumers.map((consumer, index) => (
                      <tr key={consumer.organization.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold text-sm">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {consumer.organization.name}
                          </div>
                          <div className="text-xs text-gray-500">{consumer.organization.id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              consumer.organization.status === 'ACTIVE'
                                ? 'bg-green-100 text-green-800'
                                : consumer.organization.status === 'PAUSED'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {consumer.organization.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {formatNumber(consumer.apiCalls)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {formatNumber(consumer.webhooks)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {formatNumber(consumer.users)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {formatNumber(consumer.agents)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {formatBytes(consumer.storage)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Loading State */}
      {isLoading && !summary && (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading usage analytics...</div>
        </div>
      )}
    </div>
  );
}
